import { Badge, Box, Collapse, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { theme } from "../theme/theme";
import { useDispatch, useSelector } from "react-redux";

import { setSidebar } from "../features/sidebar/sidebarSlice";
import useDisclosure from "../hooks/useDisclosure";
import { useLocation, useNavigate } from "react-router-dom";
import { getMenuIcon, getSubMenuIcon } from "./GetIcon";
import { ExpandLess, ExpandMore, Notifications } from "@mui/icons-material";
import { useGetNotificationQuery } from "../features/api_notification/notificationApi";
import helpDeskNameLogo from "../assets/images/helpdesk-icon-name.png";
import helpDeskLogo from "../assets/images/helpdesk_icon.png";

const Sidebar = () => {
  const isVisible = useSelector((state) => state.sidebar.isVisible);
  const hideSidebar = useMediaQuery("(max-width: 1639px)");
  const [drawerOpen, setDrawerOpen] = useState(isVisible);

  const dispatch = useDispatch();

  // Sync drawerOpen state with isVisible from Redux store
  useEffect(() => {
    setDrawerOpen(isVisible);
  }, [isVisible]);

  useEffect(() => {
    dispatch(setSidebar(!hideSidebar));
  }, [dispatch, hideSidebar]);

  const handleDrawerToggle = (open) => {
    setDrawerOpen(open);
    dispatch(setSidebar(open));
  };

  return (
    <>
      {hideSidebar && drawerOpen ? (
        <Drawer
          anchor="left"
          open={isVisible}
          onClose={() => handleDrawerToggle(false)}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <Stack
            sx={{
              height: "100%",
              backgroundColor: theme.palette.bgForm.black2,
              width: "280px",
              overflow: "auto",
            }}
          >
            <SidebarHeader />
            <SidebarList />
            <SidebarFooter />
          </Stack>
        </Drawer>
      ) : (
        isVisible && (
          <Stack
            sx={{
              height: "100%",
              backgroundColor: theme.palette.bgForm.black2,
              width: "280px",
              overflow: "auto",
            }}
          >
            <SidebarHeader />
            <SidebarList />
            <SidebarFooter />
          </Stack>
        )
      )}
    </>
  );
};

export default Sidebar;

const SidebarHeader = () => {
  return (
    <Stack
      sx={{
        justifyContent: "center",
        alignItems: "center",
        padding: "24px 24px 10px 24px",
        marginTop: 3,
        gap: "5px",
      }}
    >
      <img src={helpDeskLogo} alt="dotek-icon" width="74" height="50" className="logo-sidebar" />
      <Stack direction="row" gap={0.5} mt={0.5}>
        <Typography
          sx={{
            fontSize: ".9rem",
            fontWeight: "700",
            color: "#EDF2F7",
            lineHeight: "1.2",
            letterSpacing: 1,
          }}
        >
          FBTI
        </Typography>
        <Typography
          sx={{
            fontSize: ".9rem",
            fontWeight: "400",
            color: "#EDF2F7",
            lineHeight: "1.2",
            letterSpacing: 1,
          }}
        >
          HELPDESK
        </Typography>
      </Stack>
    </Stack>
  );
};

const SidebarList = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const userPermission = useSelector((state) => state?.user?.permissions);
  const { data: notificationData } = useGetNotificationQuery();
  const openNotification = notificationData?.value?.openTicketNotif || 0;
  const ticketApprovalNotification = notificationData?.value?.forApprovalClosingNotif || 0;

  const { open: userManagementOpen, onToggle: userManagementOnToggle, onClose: userManagementOnClose } = useDisclosure(!!pathname.match(/user-management/gi));
  const { open: masterListOpen, onToggle: masterListOnToggle, onClose: masterlistOnClose } = useDisclosure(!!pathname.match(/masterlist/gi));
  const { open: requestOpen, onToggle: requestOnToggle, onClose: requestOnClose } = useDisclosure(!!pathname.match(/requestor/gi));
  const { open: receiverOpen, onToggle: receiverOnToggle, onClose: receiverOnClose } = useDisclosure(!!pathname.match(/receiver/gi));
  const { open: ticketingOpen, onToggle: ticketingOnToggle, onClose: ticketingOnClose } = useDisclosure(!!pathname.match(/ticketing/gi));
  const { open: approverOpen, onToggle: approverOnToggle, onClose: approverOnClose } = useDisclosure(!!pathname.match(/approver/gi));

  const sidebarMenu = [
    {
      id: 1,
      name: "Overview",
      path: "/overview",
      icon: "EqualizerOutlined",
    },
    {
      id: 2,
      name: "User Management",
      path: "/user-management",
      icon: "PeopleOutlinedIcon",
      open: userManagementOpen,
      onToggle: () => {
        userManagementOnToggle();
        masterlistOnClose();
        requestOnClose();
        ticketingOnClose();
        approverOnClose();
      },
      sub: [
        {
          id: 1,
          menuId: 1,
          name: "User Account",
          path: "/user-management/user-account",
          icon: "PermIdentityOutlinedIcon",
        },
        {
          id: 2,
          menuId: 1,
          name: "User Role",
          path: "/user-management/user-role",
          icon: "ManageAccountsOutlinedIcon",
        },
      ],
    },
    {
      id: 3,
      name: "Masterlist",
      path: "/masterlist",
      icon: "ChecklistOutlinedIcon",
      open: masterListOpen,
      onToggle: () => {
        masterListOnToggle();
        userManagementOnClose();
        requestOnClose();
        receiverOnClose();
        ticketingOnClose();
        approverOnClose();
      },
      sub: [
        {
          id: 1,
          name: "One Charging",
          path: "/masterlist/one-charging",
          icon: "BusinessOutlinedIcon",
        },
        {
          id: 2,
          name: "Channel Setup",
          path: "/masterlist/channel-setup",
          icon: "HubOutlined",
        },
        {
          id: 3,
          name: "Category",
          path: "/masterlist/category",
          icon: "CategoryOutlined",
        },
        {
          id: 4,
          name: "Sub Category",
          path: "/masterlist/sub-category",
          icon: "AutoAwesomeMotionOutlined",
        },
        {
          id: 5,
          name: "Service Provider",
          path: "/masterlist/service-provider",
          icon: "HubOutlined",
        },
        {
          id: 6,
          name: "Contractors",
          path: "/masterlist/contractors",
          icon: "HubOutlined",
        },
        {
          id: 7,
          name: "Concern Category",
          path: "/masterlist/concern-category",
          icon: "AutoAwesomeMotionOutlined",
        },
        {
          id: 8,
          name: "Receiver Setup",
          path: "/masterlist/receiver-setup",
          icon: "ContactsOutlined",
        },
        {
          id: 9,
          name: "Approver Setup",
          path: "/masterlist/approver-setup",
          icon: "GroupsOutlined",
        },
      ],
    },
    {
      id: 4,
      name: "Requestor",
      path: "/requestor",
      icon: "DynamicFeedOutlined",
      open: requestOpen,
      onToggle: () => {
        requestOnToggle();
        userManagementOnClose();
        masterlistOnClose();
        receiverOnClose();
        ticketingOnClose();
        approverOnClose();
      },
      sub: [
        {
          id: 1,
          name: "Requestor Concerns",
          path: "/requestor/requestor-concerns",
          icon: "DiscountOutlined",
        },
      ],
    },
    {
      id: 5,
      name: "Receiver",
      path: "/receiver",
      icon: "DynamicFeedOutlined",
      open: receiverOpen,
      onToggle: () => {
        receiverOnToggle();
        requestOnClose();
        userManagementOnClose();
        masterlistOnClose();
        ticketingOnClose();
        approverOnClose();
      },
      sub: [
        {
          id: 1,
          name: "Receiver Concerns",
          path: "/receiver/receiver-concerns",
          icon: "DiscountOutlined",
        },
      ],
    },
    {
      id: 6,
      name: "Approver",
      path: "/Approver",
      icon: "CreditScoreOutlined",
      open: approverOpen,
      onToggle: () => {
        approverOnToggle();
        receiverOnClose();
        requestOnClose();
        userManagementOnClose();
        masterlistOnClose();
        ticketingOnClose();
      },
      sub: [
        {
          id: 1,
          name: "Approval",
          path: "/approver/approval",
        },
        {
          id: 2,
          name: "View Tickets",
          path: "/approver/view-tickets",
        },
      ],
    },
    {
      id: 7,
      name: "Ticketing",
      path: "/ticketing",
      icon: "ConfirmationNumberOutlined",
      open: ticketingOpen,
      onToggle: () => {
        ticketingOnToggle();
        receiverOnClose();
        requestOnClose();
        userManagementOnClose();
        masterlistOnClose();
        approverOnClose();
      },
      sub: [
        {
          id: 1,
          name: "Tickets",
          path: "/ticketing/issue-handler-tickets",
          icon: "ConfirmationNumberOutlined",
        },
      ],
    },
    {
      id: 7,
      name: "Reports",
      path: "/reports",
      icon: "AssessmentOutlined",
    },
    {
      id: 8,
      name: "Filing",
      path: "/filing",
      icon: "AttachFileOutlined",
    },
    {
      id: 9,
      name: "Generate",
      path: "/generate",
      icon: "BallotOutlined",
    },
  ];

  return (
    <Stack>
      <List
        sx={{
          marginTop: "35px",
          marginLeft: "6px",
          marginRight: "17px",
          padding: "0px",
        }}
      >
        {sidebarMenu
          .filter((item) => userPermission?.includes(item.name))
          .map((item, i) => (
            <Fragment key={i}>
              {item.sub?.length ? (
                <ListItemButton onClick={item.onToggle} selected={item.path === pathname}>
                  <ListItemIcon>{getMenuIcon(item.icon)}</ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: "600",
                      lineHeight: "24px",
                      mb: "2px",
                    }}
                  />
                  {(item?.name === "Ticketing" && openNotification) || (item?.name === "Approver" && ticketApprovalNotification) ? (
                    <Badge color="error" variant="dot">
                      {item.open ? <ExpandLess /> : <ExpandMore />}
                    </Badge>
                  ) : item.open ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </ListItemButton>
              ) : (
                <ListItemButton onClick={() => navigate(item.path)} selected={item.path === pathname}>
                  <ListItemIcon>{getMenuIcon(item.icon)}</ListItemIcon>

                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: "600",
                      lineHeight: "24px",
                      mb: "2px",
                    }}
                  />
                </ListItemButton>
              )}

              {item.sub?.filter((subItem) => userPermission.includes(subItem.name)).length > 0 && (
                <Collapse
                  in={item.open}
                  timeout="auto"
                  unmountOnExit
                  sx={{
                    maxHeight: item.open ? "250px" : "0",
                    overflowY: "auto",
                    transition: "max-height 0.3s ease-in-out",
                  }}
                >
                  <List
                    sx={{
                      marginTop: "4px",
                      marginBottom: "4px",
                      marginLeft: "25px",
                      marginRight: "2px",
                      padding: "0px",
                      maxHeight: "500px", // Set max height
                      overflowY: "auto", // Enable vertical scrolling
                    }}
                  >
                    {item.sub
                      .filter((subItem) => userPermission.includes(subItem.name))
                      .map((subItem, j) => (
                        <ListItemButton key={j} onClick={() => navigate(subItem.path)} selected={subItem.path === pathname} sx={{ padding: "2px" }}>
                          {/* <ListItemIcon>{getSubMenuIcon(subItem.icon)}</ListItemIcon> */}

                          <ListItemText
                            primary={
                              (subItem?.name === "Tickets" && openNotification) || (subItem?.name === "Approval" && ticketApprovalNotification) ? (
                                <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 0.5 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      lineHeight: "24px",
                                    }}
                                  >
                                    {subItem.name}
                                  </Typography>

                                  <Badge
                                    color="error"
                                    badgeContent={subItem?.name === "Tickets" ? openNotification : ticketApprovalNotification}
                                    sx={{
                                      "& .MuiBadge-badge": {
                                        left: 92,
                                        top: 1,
                                        fontSize: "0.65rem",
                                        fontWeight: 400,
                                      },
                                    }}
                                  />
                                </Box>
                              ) : (
                                subItem.name
                              )
                            }
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: "600",
                              lineHeight: "24px",
                              mb: "2px",
                              marginLeft: "35px",
                            }}
                          />
                        </ListItemButton>
                      ))}
                  </List>
                </Collapse>
              )}
            </Fragment>
          ))}
      </List>
    </Stack>
  );
};

const SidebarFooter = () => {
  return <div>{/* Footer */}</div>;
};
